import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, ImagePlus, Loader2, Plus, X,
} from 'lucide-react';
import { adminAPI, erpAPI } from '../../services/api';
import { Category, ProductImage } from '../../types';
import { extractList } from '../../utils/apiResponse';
import { getImageUrl } from '../../utils/imageUrl';
import SearchableSelect from '../../components/admin/SearchableSelect';
import Toast from '../../components/admin/Toast';
import './ProductFormPage.css';

function normalizeCode(value: string): string {
  return value
    .trim()
    .replace(/[/\\]+/g, '-')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function generateSku(productCode: string, color: string, size: string): string {
  const parts = [productCode, normalizeCode(color), normalizeCode(size)];
  return parts.filter(Boolean).join('-');
}

interface VariantRow {
  id?: number;
  sku: string;
  color: string;
  size: string;
  selling_price: string;
  unit_cost?: string;
  wholesale_price?: string;
  barcode?: string;
  image?: number | null;
  is_active: boolean;
  _key: string;
  _skuAutoGenerated: boolean;
}

interface ColorImageFile {
  file?: File;
  preview?: string;
  name: string;
  is_main: boolean;
  existingId?: number;
  existingUrl?: string;
}

const COLOR_PRESETS = ['Black', 'White', 'Red', 'Blue', 'Green', 'Yellow', 'Pink', 'Purple', 'Orange', 'Gray', 'Brown', 'Navy', 'Beige', 'Cream', 'Gold', 'Silver'];
const SIZE_PRESETS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];

const ProductFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [availableColorsData, setAvailableColorsData] = useState<{ id?: number, name: string, hex_code?: string, is_active?: boolean }[]>([]);
  const [availableSizesData, setAvailableSizesData] = useState<{ id?: number, name: string, is_active?: boolean }[]>([]);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({ show: false, message: '', type: 'success' });

  const [form, setForm] = useState({
    sku: '', name_bn: '', name_en: '', slug: '', category: '', brand: '',
    summary_bn: '', summary_en: '', description_bn: '', description_en: '',
    youtube_url: '', is_active: true, is_featured: false, free_delivery: false,
    size_chart_image: null as File | null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [colors, setColors] = useState<string[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [colorInput, setColorInput] = useState('');
  const [sizeInput, setSizeInput] = useState('');
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const colorDropdownRef = useRef<HTMLDivElement>(null);
  const sizeDropdownRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const sizeInputRef = useRef<HTMLInputElement>(null);
  const [colorFilter, setColorFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');

  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [colorImages, setColorImages] = useState<Record<string, ColorImageFile[]>>({});
  const [expandedColors, setExpandedColors] = useState<Record<string, boolean>>({});

  useEffect(() => { loadData(); }, [id]);

  useEffect(() => { rebuildVariants(); }, [colors, sizes, form.sku]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catRes, brandRes, colorRes, sizeRes] = await Promise.all([
        adminAPI.getCategories({ page_size: 100, ordering: 'name_en' }),
        erpAPI.list('brand-options'),
        erpAPI.list('color-options').catch(() => ({ data: [] })),
        erpAPI.list('size-options').catch(() => ({ data: [] })),
      ]);
      setCategories(extractList<Category>(catRes));
      setBrands(Array.isArray(brandRes.data) ? brandRes.data : (brandRes.data?.results || []));

      const cData = Array.isArray(colorRes.data) ? colorRes.data : (colorRes.data?.results || []);
      const sData = Array.isArray(sizeRes.data) ? sizeRes.data : (sizeRes.data?.results || []);
      setAvailableColorsData(cData.filter((c: any) => c.is_active));
      setAvailableSizesData(sData.filter((s: any) => s.is_active));

      if (id) {
        const res = await adminAPI.getProduct(Number(id));
        const product = res.data;
        setForm({
          sku: product.sku || '', name_bn: product.name_bn || '', name_en: product.name_en || '',
          slug: product.slug || '', category: product.category?.id || product.category || '',
          brand: product.brand || '',
          summary_bn: product.summary_bn || '', summary_en: product.summary_en || '',
          description_bn: product.description_bn || '', description_en: product.description_en || '',
          youtube_url: product.youtube_url || '',
          is_active: product.is_active ?? true, is_featured: product.is_featured ?? false,
          free_delivery: product.free_delivery ?? false,
          size_chart_image: null,
        });
        const attrs = product.attributes || {};
        const loadedColors = attrs.colors?.options || [];
        const loadedSizes = attrs.sizes?.options || [];
        setColors(loadedColors);
        setSizes(loadedSizes);

        if (product.variants && product.variants.length > 0) {
          const loadedVariants: VariantRow[] = product.variants.map((v: any) => ({
            id: v.id,
            sku: v.sku || generateSku(product.sku || '', v.color || '', v.size || ''),
            color: v.color || '',
            size: v.size || '',
            selling_price: v.selling_price != null ? String(v.selling_price) : '',
            unit_cost: v.unit_cost != null ? String(v.unit_cost) : '',
            wholesale_price: v.wholesale_price != null ? String(v.wholesale_price) : '',
            barcode: v.barcode || '',
            image: v.image_id != null ? v.image_id : null,
            is_active: v.is_active ?? true,
            _key: `${v.color || ''}_${v.size || ''}`,
            _skuAutoGenerated: false,
          }));
          setVariants(loadedVariants);
        }

        const imagesByColor: Record<string, ColorImageFile[]> = {};
        const cmList = (product as any).color_media || [];
        for (const cm of cmList) {
          imagesByColor[cm.color] = (cm.gallery_images || []).map((gi: any) => ({
            existingId: gi.id,
            existingUrl: gi.image_url || gi.image,
            name: gi.name || '',
            is_main: gi.is_main_for_color || false,
          }));
        }
        setColorImages(imagesByColor);
      }
    } catch (e) {
      setToast({ show: true, message: 'Failed to load data', type: 'error' });
    } finally { setLoading(false); }
  };

  const rebuildVariants = () => {
    if (colors.length === 0) return;
    setVariants(prev => {
      const existingMap = new Map(prev.map(v => [`${v.color}_${v.size}`, v]));
      const productCode = form.sku.trim().toUpperCase();
      const sizeList = sizes.length ? sizes : [''];
      const generated: VariantRow[] = [];
      const seen = new Set<string>();
      colors.forEach(color => {
        sizeList.forEach(size => {
          const key = `${color}_${size}`;
          if (seen.has(key)) return;
          seen.add(key);
          const existing = existingMap.get(key);
          if (existing) {
            generated.push(existing._skuAutoGenerated && productCode
              ? { ...existing, sku: generateSku(productCode, color, size) }
              : existing);
          } else {
            generated.push({
              sku: productCode ? generateSku(productCode, color, size) : '',
              color, size, selling_price: '', is_active: true, _key: key, _skuAutoGenerated: !!productCode,
            });
          }
        });
      });
      prev.forEach(v => {
        if (v._key.startsWith('manual_')) generated.push({ ...v });
      });
      return generated;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    if (type === 'file') {
      const files = (e.target as HTMLInputElement).files;
      if (files?.[0]) setForm(prev => ({ ...prev, [name]: files[0] }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const generateSlug = () => {
    const slug = form.name_en.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setForm(prev => ({ ...prev, slug }));
  };

  const addColor = async (color: string) => {
    const val = color.trim();
    if (!val || colors.some(c => c.toLowerCase() === val.toLowerCase())) return;

    const exists = availableColorsData.some(c => c.name.toLowerCase() === val.toLowerCase());
    if (!exists) {
      try {
        const res = await erpAPI.create('color-options', { name: val, is_active: true });
        setAvailableColorsData(prev => [...prev, res.data]);
      } catch (e: any) {
        setToast({ show: true, message: 'Failed to create new color', type: 'error' });
        return;
      }
    }

    setColors(prev => [...prev, val]);
    setColorInput(''); setColorFilter('');
  };

  const removeColor = (color: string) => {
    setColors(prev => prev.filter(c => c !== color));
    setVariants(prev => prev.filter(v => v.color !== color));
    const next = { ...colorImages };
    delete next[color];
    setColorImages(next);
  };

  const addSize = async (size: string) => {
    const val = size.trim();
    if (!val || sizes.some(s => s.toLowerCase() === val.toLowerCase())) return;

    const exists = availableSizesData.some(s => s.name.toLowerCase() === val.toLowerCase());
    if (!exists) {
      try {
        const res = await erpAPI.create('size-options', { name: val, is_active: true });
        setAvailableSizesData(prev => [...prev, res.data]);
      } catch (e: any) {
        setToast({ show: true, message: 'Failed to create new size', type: 'error' });
        return;
      }
    }

    setSizes(prev => [...prev, val]);
    setSizeInput(''); setSizeFilter('');
  };

  const removeSize = (size: string) => {
    setSizes(prev => prev.filter(s => s !== size));
    setVariants(prev => prev.filter(v => v.size !== size));
  };

  const availableColors = availableColorsData.map(c => c.name).filter(c => !colors.some(oc => oc.toLowerCase() === c.toLowerCase()));
  const availableSizes = availableSizesData.map(s => s.name).filter(s => !sizes.some(os => os.toLowerCase() === s.toLowerCase()));

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (colorDropdownRef.current && !colorDropdownRef.current.contains(e.target as Node) &&
        colorInputRef.current && !colorInputRef.current.contains(e.target as Node)) setShowColorDropdown(false);
      if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(e.target as Node) &&
        sizeInputRef.current && !sizeInputRef.current.contains(e.target as Node)) setShowSizeDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const updateVariant = (key: string, patch: Partial<VariantRow>) => {
    setVariants(prev => prev.map(v => {
      if (v._key !== key) return v;
      const updated = { ...v, ...patch };
      if ('sku' in patch) updated._skuAutoGenerated = false;
      return updated;
    }));
  };

  const removeVariantRow = (key: string) => {
    setVariants(prev => prev.filter(v => v._key !== key));
  };

  const addManualVariant = () => {
    const k = `manual_${Date.now()}`;
    const productCode = form.sku.trim().toUpperCase();
    setVariants(prev => [...prev, {
      sku: productCode ? generateSku(productCode, '', '') : '',
      color: '', size: '', selling_price: '', is_active: true, _key: k, _skuAutoGenerated: !!productCode,
    }]);
  };

  const skuValidationErrors = (() => {
    const skus = variants.filter(v => v.sku.trim()).map(v => v.sku.trim().toUpperCase());
    const dupeSet = new Set<string>();
    const seen = new Set<string>();
    for (const s of skus) { if (seen.has(s)) dupeSet.add(s); seen.add(s); }
    return dupeSet;
  })();

  // ── Color Image handlers ──
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_IMAGE_DIM = 4000;
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  const validateAndAddImages = (files: FileList | null, color: string, isMain: boolean) => {
    if (!files || files.length === 0) return;
    const valid: { file: File; preview: string; name: string; is_main: boolean }[] = [];
    const errors: string[] = [];

    for (const file of Array.from(files)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" is not a supported image type (JPEG, PNG, WebP, GIF only).`);
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        errors.push(`"${file.name}" exceeds the 5MB size limit.`);
        continue;
      }
      valid.push({
        file,
        preview: URL.createObjectURL(file),
        name: file.name.replace(/\.[^.]+$/, ''),
        is_main: false,
      });
    }

    if (errors.length > 0) {
      setToast({ show: true, message: errors.join(' '), type: 'error' });
    }

    if (valid.length === 0) return;

    setColorImages(prev => {
      let images = [...(prev[color] || [])];
      if (isMain) {
        const mainImg = { ...valid[0], is_main: true };
        images = [mainImg, ...images.filter(i => !i.is_main), ...valid.slice(1).map(v => ({ ...v, is_main: false }))];
        images.forEach((img, i) => { if (i !== 0) img.is_main = false; });
      } else {
        images.push(...valid);
      }
      return { ...prev, [color]: images };
    });
    setExpandedColors(prev => ({ ...prev, [color]: true }));
  };

  const toggleColorExpanded = (color: string) => {
    setExpandedColors(prev => ({ ...prev, [color]: !prev[color] }));
  };

  const addImageToColor = (color: string, isMain: boolean) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = ALLOWED_TYPES.join(','); input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);
    input.onchange = (e: any) => {
      validateAndAddImages(e.target?.files, color, isMain);
      document.body.removeChild(input);
    };
    input.click();
  };

  const removeColorImage = (color: string, idx: number) => {
    setColorImages(prev => {
      const images = [...(prev[color] || [])];
      const img = images[idx];
      if (img.preview && !img.existingUrl) URL.revokeObjectURL(img.preview);
      images.splice(idx, 1);
      return { ...prev, [color]: images };
    });
  };

  const setMainColorImage = (color: string, idx: number) => {
    setColorImages(prev => {
      const images = (prev[color] || []).map((img, i) => ({ ...img, is_main: i === idx }));
      return { ...prev, [color]: images };
    });
  };

  const reorderColorImage = (color: string, from: number, to: number) => {
    setColorImages(prev => {
      const images = [...(prev[color] || [])];
      if (to < 0 || to >= images.length) return prev;
      const [moved] = images.splice(from, 1);
      images.splice(to, 0, moved);
      return { ...prev, [color]: images };
    });
  };

  // ── Validation ──
  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.sku.trim()) errs.sku = 'Required';
    if (!form.name_bn.trim()) errs.name_bn = 'Required';
    if (!form.name_en.trim()) errs.name_en = 'Required';
    if (!form.slug.trim()) errs.slug = 'Required';
    if (!form.category) errs.category = 'Required';
    if (colors.length === 0) errs.colors = 'At least one color is required';

    const skus = variants.filter(v => v.sku.trim()).map(v => v.sku.trim().toUpperCase());
    if (new Set(skus).size !== skus.length) {
      errs.variants = 'Duplicate SKUs found. Each SKU must be unique.';
    }

    for (const v of variants) {
      const sp = v.selling_price.trim();
      if (sp !== '' && (isNaN(Number(sp)) || Number(sp) < 0)) {
        errs.variants = 'Each variant selling price must be a valid non-negative number.';
        break;
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('sku', form.sku.trim().toUpperCase());
      fd.append('name_bn', form.name_bn);
      fd.append('name_en', form.name_en);
      fd.append('slug', form.slug);
      fd.append('category', form.category);
      if (form.brand) fd.append('brand', form.brand);
      fd.append('summary_bn', form.summary_bn || '');
      fd.append('summary_en', form.summary_en || '');
      fd.append('description_bn', form.description_bn || '');
      fd.append('description_en', form.description_en || '');
      fd.append('is_active', String(form.is_active));
      fd.append('is_featured', String(form.is_featured));
      fd.append('free_delivery', String(form.free_delivery));
      fd.append('youtube_url', form.youtube_url.trim());
      fd.append('attributes', JSON.stringify({
        colors: { enabled: colors.length > 0, options: colors },
        sizes: { enabled: sizes.length > 0, options: sizes },
        weights: { enabled: false, options: [] },
      }));

      const variantData = variants.map(v => ({
        id: v.id, sku: v.sku.trim().toUpperCase(), color: v.color, size: v.size,
        selling_price: v.selling_price.trim() === '' ? 0 : Number(v.selling_price),
        barcode: v.barcode || null,
        image: v.image != null ? v.image : null,
        is_active: v.is_active,
      }));
      if (variantData.length > 0) fd.append('variants', JSON.stringify(variantData));

      const colorMediaData = colors.map(c => ({
        color: c,
        display_order: colors.indexOf(c),
        gallery_images: (colorImages[c] || []).map((img, idx) => {
          if (img.existingId) return { id: img.existingId, name: img.name, is_main_for_color: img.is_main, display_order: idx };
          return { name: img.name, is_main_for_color: img.is_main, display_order: idx };
        }),
      }));
      fd.append('color_media', JSON.stringify(colorMediaData));

      if (form.size_chart_image instanceof File) fd.append('size_chart_image', form.size_chart_image);

      let savedProduct: any;
      if (isEditing) {
        const res = await adminAPI.updateProduct(Number(id), fd);
        savedProduct = res.data;
      } else {
        const res = await adminAPI.createProduct(fd);
        savedProduct = res.data;
      }

      if (savedProduct?.id) {
        const uploads: { file: File; name: string; color: string; is_primary?: boolean }[] = [];
        for (const [color, images] of Object.entries(colorImages)) {
          for (const img of images) {
            if (img.file) uploads.push({ file: img.file, name: img.name, color, is_primary: img.is_main });
          }
        }
        if (uploads.length > 0) await adminAPI.uploadProductImages(savedProduct.id, uploads);
      }

      setToast({ show: true, message: `Product ${isEditing ? 'updated' : 'created'} successfully`, type: 'success' });
      setTimeout(() => navigate('/admin/catalog?tab=products'), 1000);
    } catch (error: any) {
      const data = error?.response?.data;
      if (data) setErrors(typeof data === 'object' ? data : { general: String(data) });
      setToast({ show: true, message: 'Failed to save product', type: 'error' });
    } finally { setSaving(false); }
  };

  // ── Build grouped variant rows ──
  const colorGroupRows: { color: string; variants: VariantRow[] }[] = [];
  const colorOrder = [...new Set(variants.map(v => v.color))];
  for (const color of colorOrder) {
    const group = variants.filter(v => v.color === color);
    if (group.length > 0) colorGroupRows.push({ color, variants: group });
  }

  if (loading) {
    return (
      <div className="pfp-page">
        <div className="pfp-loading"><Loader2 size={24} className="pfp-spin" /> Loading product form…</div>
      </div>
    );
  }

  return (
    <div className="pfp-page">
      <div className="pfp-header">
        <button className="pfp-back" onClick={() => navigate('/admin/catalog?tab=products')}><ArrowLeft size={18} /></button>
        <div>
          <h1>{isEditing ? 'Edit Product' : 'Add Product'}</h1>
          <p className="pfp-subtitle">{isEditing ? 'Update product details, variants and images' : 'Create a new product with variants and images'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="pfp-grid">
          <div className="pfp-left">
            <section className="pfp-card">
              <h2>Basic Information</h2>
              <div className="pfp-field">
                <label>Product Code *</label>
                <input name="sku" value={form.sku} onChange={handleChange} placeholder="TS001" className={errors.sku ? 'pfp-error' : ''} />
                {errors.sku && <span className="pfp-error-text">{errors.sku}</span>}
              </div>
              <div className="pfp-row">
                <div className="pfp-field">
                  <label>Name (Bangla) *</label>
                  <input name="name_bn" value={form.name_bn} onChange={handleChange} placeholder="পণ্যের নাম" className={errors.name_bn ? 'pfp-error' : ''} />
                  {errors.name_bn && <span className="pfp-error-text">{errors.name_bn}</span>}
                </div>
                <div className="pfp-field">
                  <label>Name (English) *</label>
                  <input name="name_en" value={form.name_en} onChange={handleChange} onBlur={generateSlug} placeholder="Product name" className={errors.name_en ? 'pfp-error' : ''} />
                  {errors.name_en && <span className="pfp-error-text">{errors.name_en}</span>}
                </div>
              </div>
              <div className="pfp-field">
                <label>Slug *</label>
                <input name="slug" value={form.slug} onChange={handleChange} placeholder="product-url-slug" className={errors.slug ? 'pfp-error' : ''} />
                {errors.slug && <span className="pfp-error-text">{errors.slug}</span>}
              </div>
              <div className="pfp-row">
                <div className="pfp-field">
                  <label>Category *</label>
                  <SearchableSelect value={form.category} onChange={v => setForm(prev => ({ ...prev, category: v }))} placeholder="Select category…" options={categories.map(c => ({ value: c.id, label: c.name_en }))} />
                  {errors.category && <span className="pfp-error-text">{errors.category}</span>}
                </div>
                <div className="pfp-field">
                  <label>Brand</label>
                  <SearchableSelect value={form.brand} onChange={v => setForm(prev => ({ ...prev, brand: v }))} placeholder="Select brand…" options={brands.map((b: any) => ({ value: b.id, label: b.name }))} />
                </div>
              </div>
              <div className="pfp-field">
                <label>YouTube URL</label>
                <input name="youtube_url" value={form.youtube_url} onChange={handleChange} placeholder="https://youtube.com/watch?v=..." />
              </div>
            </section>

            <section className="pfp-card">
              <h2>Descriptions</h2>
              <div className="pfp-row">
                <div className="pfp-field">
                  <label>Summary (Bangla)</label>
                  <textarea name="summary_bn" value={form.summary_bn} onChange={handleChange} rows={3} placeholder="সংক্ষিপ্ত বিবরণ..." />
                </div>
                <div className="pfp-field">
                  <label>Summary (English)</label>
                  <textarea name="summary_en" value={form.summary_en} onChange={handleChange} rows={3} placeholder="Brief summary..." />
                </div>
              </div>
              <div className="pfp-row">
                <div className="pfp-field">
                  <label>Description (Bangla) *</label>
                  <textarea name="description_bn" value={form.description_bn} onChange={handleChange} rows={6} placeholder="পণ্যের বিস্তারিত বর্ণনা..." className={errors.description_bn ? 'pfp-error' : ''} />
                  {errors.description_bn && <span className="pfp-error-text">Required</span>}
                </div>
                <div className="pfp-field">
                  <label>Description (English) *</label>
                  <textarea name="description_en" value={form.description_en} onChange={handleChange} rows={6} placeholder="Detailed product description..." className={errors.description_en ? 'pfp-error' : ''} />
                  {errors.description_en && <span className="pfp-error-text">Required</span>}
                </div>
              </div>
            </section>
          </div>

          <div className="pfp-right">
            <section className="pfp-card">
              <h2>Size Chart & Media</h2>
              <div className="pfp-field">
                <label>Size Chart Image</label>
                <input type="file" name="size_chart_image" onChange={handleChange} accept="image/*" />
              </div>
              <p className="pfp-section-hint">
                Product images are managed per color below. Each color carries its own main image and gallery — there is no separate product main image.
              </p>
            </section>

            <section className="pfp-card">
              <h2>Status & Visibility</h2>
              <div className="pfp-toggle-row">
                <label className="pfp-toggle">
                  <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                  <span className="pfp-toggle-track"><span className="pfp-toggle-knob" /></span><span>Active Product</span>
                </label>
              </div>
              <div className="pfp-toggle-row">
                <label className="pfp-toggle">
                  <input type="checkbox" name="is_featured" checked={form.is_featured} onChange={handleChange} />
                  <span className="pfp-toggle-track"><span className="pfp-toggle-knob" /></span><span>Featured Product</span>
                </label>
              </div>
              <div className="pfp-toggle-row">
                <label className="pfp-toggle">
                  <input type="checkbox" name="free_delivery" checked={form.free_delivery} onChange={handleChange} />
                  <span className="pfp-toggle-track"><span className="pfp-toggle-knob" /></span><span>Free Home Delivery</span>
                </label>
              </div>
            </section>
          </div>
        </div>

        {/* ── COLOR & SIZE SELECTION ── */}
        <section className="pfp-card pfp-variants-card">
          <div className="pfp-variants-head">
            <div>
              <h2>Variants (Colors &amp; Sizes)</h2>
              <p className="pfp-section-hint">Pick colors (required) and optionally sizes. Variants auto-generate in real time; set each variant&apos;s selling price and SKU directly in the table below. Unit cost and wholesale price are managed from the Purchase page.</p>
            </div>
          </div>

          <div className="pfp-variant-inputs">
            <div className="pfp-vattr">
              <label>Colors {errors.colors && <span className="pfp-error-text">* Required</span>}</label>
              <div className="pfp-dropdown-wrap">
                <input ref={colorInputRef} value={colorInput}
                  onChange={e => { setColorInput(e.target.value); setColorFilter(e.target.value); setShowColorDropdown(true); }}
                  onFocus={() => setShowColorDropdown(true)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (colorInput.trim() && !colors.some(c => c.toLowerCase() === colorInput.trim().toLowerCase())) addColor(colorInput.trim()); } }}
                  placeholder="Type color name or pick below…" />
                {showColorDropdown && (
                  <div ref={colorDropdownRef} className="pfp-dropdown">
                    {colorInput.trim() && !colors.some(c => c.toLowerCase() === colorInput.trim().toLowerCase()) && !availableColorsData.some(c => c.name.toLowerCase() === colorInput.trim().toLowerCase()) && (
                      <button type="button" className="pfp-dropdown-add" onClick={() => addColor(colorInput.trim())}><Plus size={14} /> Add &quot;{colorInput.trim()}&quot;</button>
                    )}
                    {availableColors.filter(c => c.toLowerCase().includes(colorFilter.toLowerCase())).map(c => {
                      const colorObj = availableColorsData.find(d => d.name === c);
                      const swatchColor = colorObj?.hex_code ? colorObj.hex_code : c.toLowerCase();
                      return (
                        <button key={c} type="button" className="pfp-dropdown-item" onClick={() => addColor(c)}>
                          <span className="pfp-chip-swatch" style={{ background: swatchColor }} />{c}
                        </button>
                      );
                    })}
                    {availableColors.filter(c => c.toLowerCase().includes(colorFilter.toLowerCase())).length === 0 && <div className="pfp-dropdown-empty">No matching colors</div>}
                  </div>
                )}
              </div>
              <div className="pfp-chips">
                {colors.map(c => {
                  const colorObj = availableColorsData.find(d => d.name === c);
                  const swatchColor = colorObj?.hex_code ? colorObj.hex_code : c.toLowerCase();
                  return (
                    <span key={c} className="pfp-chip pfp-chip-color">
                      <span className="pfp-chip-swatch" style={{ background: swatchColor }} />{c}
                      <button type="button" onClick={() => removeColor(c)}><X size={12} /></button>
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="pfp-vattr">
              <label>Sizes <span className="pfp-optional">(optional)</span></label>
              <div className="pfp-dropdown-wrap">
                <input ref={sizeInputRef} value={sizeInput}
                  onChange={e => { setSizeInput(e.target.value); setSizeFilter(e.target.value); setShowSizeDropdown(true); }}
                  onFocus={() => setShowSizeDropdown(true)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (sizeInput.trim() && !sizes.some(s => s.toLowerCase() === sizeInput.trim().toLowerCase())) addSize(sizeInput.trim()); } }}
                  placeholder="Type size or pick below…" />
                {showSizeDropdown && (
                  <div ref={sizeDropdownRef} className="pfp-dropdown">
                    {sizeInput.trim() && !sizes.some(s => s.toLowerCase() === sizeInput.trim().toLowerCase()) && !availableSizesData.some(s => s.name.toLowerCase() === sizeInput.trim().toLowerCase()) && (
                      <button type="button" className="pfp-dropdown-add" onClick={() => addSize(sizeInput.trim())}><Plus size={14} /> Add &quot;{sizeInput.trim()}&quot;</button>
                    )}
                    {availableSizes.filter(s => s.toLowerCase().includes(sizeFilter.toLowerCase())).map(s => (
                      <button key={s} type="button" className="pfp-dropdown-item" onClick={() => addSize(s)}>{s}</button>
                    ))}
                    {availableSizes.filter(s => s.toLowerCase().includes(sizeFilter.toLowerCase())).length === 0 && <div className="pfp-dropdown-empty">No matching sizes</div>}
                  </div>
                )}
              </div>
              <div className="pfp-chips">
                {sizes.map(s => (
                  <span key={s} className="pfp-chip pfp-chip-size">
                    {s}<button type="button" onClick={() => removeSize(s)}><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {colors.length > 0 && variants.length > 0 && (
            <div className="pfp-variant-combos">
              <p><strong>{variants.length}</strong> variant{variants.length > 1 ? 's' : ''} ({colors.join(', ')}{sizes.length > 0 ? ` × ${sizes.join(', ')}` : ''}). SKUs auto-generate from Product Code + normalized color/size codes.</p>
            </div>
          )}
        </section>

        {/* ── VARIANT TABLE (Grouped by Color with Integrated Images) ── */}
        <section className="pfp-card pfp-table-section">
          <div className="pfp-table-header">
            <h2>Variants {variants.length > 0 && <>— {variants.length} total</>}</h2>
            <div className="pfp-table-actions">
              <button type="button" className="pfp-btn pfp-btn-sm" onClick={addManualVariant}><Plus size={14} /> Add Row</button>
            </div>
          </div>

          {variants.length === 0 ? (
            <div className="pfp-table-empty">
              <p>No variants yet. Select colors above, or click <strong>Add Row</strong> to create one manually.</p>
            </div>
          ) : (
            <div className="pfp-table-wrap">
              <table className="pfp-variant-table pfp-grouped-table">
                <thead>
                  <tr>
                    <th>Color</th><th>Size</th><th>SKU</th><th>Selling Price (৳)</th><th>Status</th><th>Color Images</th>
                  </tr>
                </thead>
                <tbody>
                  {colorGroupRows.map(group => {
                    const images = colorImages[group.color] || [];
                    const rowSpan = group.variants.length;
                    const expanded = expandedColors[group.color] || false;
                    const hasDupSku = group.variants.some(v => {
                      const s = v.sku.trim().toUpperCase();
                      return s && skuValidationErrors.has(s);
                    });
                    return group.variants.map((v, idx) => {
                      const skuUpper = v.sku.trim().toUpperCase();
                      const isDup = skuUpper && skuValidationErrors.has(skuUpper);
                      return (
                        <tr key={v._key} className={`${!v.is_active ? 'pfp-tr-inactive' : ''} ${hasDupSku ? 'pfp-tr-has-dup' : ''}`}>
                          {idx === 0 && (
                            <td className="pfp-color-group-cell" rowSpan={rowSpan}>
                              <span className="pfp-chip-swatch pfp-cg-swatch" style={{ background: (availableColorsData.find(d => d.name === group.color)?.hex_code || group.color.toLowerCase()) }} />
                              {group.color}
                            </td>
                          )}
                          <td>{v.size || <span className="pfp-placeholder">—</span>}</td>
                          <td>
                            <div className="pfp-sku-wrap">
                              <input type="text" value={v.sku}
                                onChange={e => updateVariant(v._key, { sku: e.target.value })}
                                className={`pfp-sku-input ${isDup ? 'pfp-input-error' : ''}`}
                                placeholder="Auto-generated" />
                              {isDup && <span className="pfp-input-err-icon" title="Duplicate SKU">!</span>}
                            </div>
                          </td>
                          <td>
                            <input
                              type="number" min="0" step="0.01"
                              className="pfp-cell-input pfp-cell-price"
                              value={v.selling_price}
                              placeholder="0"
                              onChange={e => updateVariant(v._key, { selling_price: e.target.value })}
                            />
                          </td>
                          <td>
                            <label className="pfp-toggle pfp-toggle-sm">
                              <input type="checkbox" checked={v.is_active}
                                onChange={e => updateVariant(v._key, { is_active: e.target.checked })} />
                              <span className="pfp-toggle-track"><span className="pfp-toggle-knob" /></span>
                            </label>
                          </td>
                          {idx === 0 && (
                            <td className="pfp-color-image-cell" rowSpan={rowSpan}>
                              <div className="pfp-color-image-panel">
                                <div className="pfp-cip-actions">
                                  <button type="button" className="pfp-cip-btn" onClick={() => addImageToColor(group.color, true)}><ImagePlus size={13} /> Main</button>
                                  <button type="button" className="pfp-cip-btn" onClick={() => addImageToColor(group.color, false)}><Plus size={13} /> Gallery</button>
                                  <button type="button" className="pfp-cip-btn pfp-cip-btn-expand" onClick={() => toggleColorExpanded(group.color)}>
                                    {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {images.length}
                                  </button>
                                </div>
                                {images.length > 0 && (
                                  <div className={`pfp-cip-thumbs ${expanded ? 'pfp-cip-thumbs-exp' : ''}`}>
                                    {images.map((img, imgIdx) => (
                                      <div key={imgIdx} className={`pfp-cip-thumb ${img.is_main ? 'pfp-cip-thumb-main' : ''}`}>
                                        <div className="pfp-cip-img-wrap">
                                          <img src={img.existingUrl || img.preview || ''} alt={img.name} />
                                          {img.is_main && <span className="pfp-cip-main-badge">Main</span>}
                                        </div>
                                        {expanded && (
                                          <>
                                            <input
                                              type="text"
                                              className="pfp-cip-name-input"
                                              value={img.name}
                                              onChange={e => {
                                                const newImages = [...(colorImages[group.color] || [])];
                                                newImages[imgIdx] = { ...newImages[imgIdx], name: e.target.value };
                                                setColorImages(prev => ({ ...prev, [group.color]: newImages }));
                                              }}
                                              placeholder="Image name"
                                            />
                                            <div className="pfp-cip-thumb-actions">
                                              {!img.is_main && <button type="button" onClick={() => setMainColorImage(group.color, imgIdx)} title="Set as main">★</button>}
                                              {imgIdx > 0 && <button type="button" onClick={() => reorderColorImage(group.color, imgIdx, imgIdx - 1)}>←</button>}
                                              {imgIdx < images.length - 1 && <button type="button" onClick={() => reorderColorImage(group.color, imgIdx, imgIdx + 1)}>→</button>}
                                              <button type="button" className="pfp-cip-del" onClick={() => removeColorImage(group.color, imgIdx)}><X size={10} /></button>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {images.length === 0 && <div className="pfp-cip-empty">No images</div>}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          )}
          {errors.variants && <div className="pfp-error-text" style={{ marginTop: 8 }}>{errors.variants}</div>}
        </section>

        <div className="pfp-actions">
          <button type="button" className="pfp-btn pfp-btn-cancel" onClick={() => navigate('/admin/catalog?tab=products')} disabled={saving}>Cancel</button>
          <button type="submit" className="pfp-btn pfp-btn-save" disabled={saving}>
            {saving ? <><Loader2 size={16} className="pfp-spin" /> Saving…</> : <><CheckCircle2 size={16} /> {isEditing ? 'Update Product' : 'Create Product'}</>}
          </button>
        </div>
      </form>

      {toast.show && <Toast {...toast} onClose={() => setToast({ ...toast, show: false })} />}
    </div>
  );
};

export default ProductFormPage;